<template>
  <v-row>
    <v-col cols="12">
      <v-card class="user-card" flat>
        <!-- HEADER BAND -->
        <div class="user-header pl-base-bg">
          <div class="user-header-icon">
            <v-icon color="#fff" size="22">mdi-account-edit</v-icon>
          </div>
          <div>
            <div class="user-title">Edit User</div>
            <div class="user-subtitle">{{ form.name || "Update user account" }}</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="user-section-label">Account Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Full Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                outlined
                dense
                :rules="[rules.required, rules.email]"
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.username"
                label="Username (optional)"
                outlined
                dense
                :rules="[rules.username]"
                prepend-inner-icon="mdi-at"
                @input="form.username = form.username.toLowerCase()"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.password"
                label="Password"
                outlined
                dense
                :append-icon="passwordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="passwordVisible = !passwordVisible"
                :type="passwordVisible ? 'text' : 'password'"
                placeholder="Leave blank to keep current"
                prepend-inner-icon="mdi-lock"
              />
            </v-col>
          </v-row>

          <div class="user-section-label mt-2">Access &amp; Status</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.userType"
                :items="userTypes"
                label="User Type"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-account-cog"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDate"
                    label="Payment Expiry"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker
                  v-model="form.paymentExpiryDate"
                  @change="handleDateChange"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch
                v-model="form.isActive"
                :label="form.isActive ? 'Active User' : 'Inactive User'"
                inset
                color="green"
                reverse
                class="mt-0"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="canDelete" cols="4" sm="auto">
              <v-btn
                block
                color="error"
                outlined
                @click="confirmDelete = true"
              >
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn
                block
                class="user-save-btn"
                :loading="loading"
                @click="submit"
              >
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this user?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "EditUser",
  components: { GlobalDialog },
  data() {
    return {
      valid: false,
      loading: false,
      passwordVisible: false,
      confirmDelete: false,
      dateMenu: false,
      form: {
        name: "",
        email: "",
        username: "",
        phone: "",
        password: "",
        userType: "customer",
        isActive: true,
        paymentExpiryDate: null,
      },
      userTypes: ["admin", "customer"],
      rules: {
        required: (v) => !!v || "Required",
        email: (v) => /.+@.+\..+/.test(v) || "Invalid email",
        username: (v) =>
          !v || /^[a-z0-9_.]{4,20}$/.test(v) ||
          "4-20 chars: lowercase letters, numbers, underscore, dot",
      },
    };
  },
  async created() {
    const userId = this.$route.params.id;
    await this.loadUser(userId);
  },
  computed: {
    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },
    loggedInUser() {
      return this.$store.state.auth.user;
    },
    canDelete() {
      const userId = this.$route.params.id;
      return this.loggedInUser && this.loggedInUser._id !== userId;
    },
    formattedDate() {
      if (!this.form.paymentExpiryDate) return "";

      const date = new Date(this.form.paymentExpiryDate);

      return date.toLocaleDateString("en-GB", {
        timeZone: "Asia/Kolkata",
      });
    },
  },
  methods: {
    ...mapActions("users", [
      "getUsers",
      "getUserById",
      "updateUser",
      "deleteUser",
    ]),

    async loadUser(id) {
      try {
        const user = await this.getUserById(id);
        const { name, email, username, phone, userType, isActive, paymentExpiryDate } =
          user;

        this.form = {
          name,
          email,
          username: username || "",
          phone,
          password: "",
          userType,
          isActive,
          paymentExpiryDate: paymentExpiryDate
            ? new Date(paymentExpiryDate).toISOString().split("T")[0]
            : "",
        };
      } catch (err) {
        this.$toast?.error("Failed to load user data");
        this.$router.push("/users");
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;

      const userId = this.$route.params.id;
      const payload = { ...this.form };
      if (!payload.password) delete payload.password;

      try {
        await this.updateUser({ id: userId, payload });
        this.$toast?.success("User updated successfully");
        this.$router.push("/users");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update user",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    async delete() {
      const userId = this.$route.params.id;

      try {
        await this.deleteUser(userId);
        this.$toast?.success("User deleted successfully");
        this.$router.push("/users");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete user",
        );
      } finally {
        this.confirmDelete = false;
      }
    },

    handleDateChange(val) {
      this.form.paymentExpiryDate = val;
      this.dateMenu = false;
    },
  },
};
</script>

<style scoped>
.user-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.user-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.user-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.user-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.user-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.user-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
